import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import './App-processing.css';
import { ExtractedReport } from './types/upload';
import { UploadSession } from './components/UploadFlow/UploadSession';
import { TrendsView } from './components/TrendsView';
import { ReportsManager } from './components/ReportsManager';
import { TrendsDataProvider } from './contexts/TrendsDataContext';
import { SimpleAuthProvider, useSimpleAuth } from './contexts/SimpleAuthContext';
import { LandingPage } from './components/LandingPage';
import { CostTracker } from './components/CostTracker';
import { SessionStatus } from './components/SessionStatus';
import { Header } from './components/Header';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { ForgotPasswordForm } from './components/auth/ForgotPasswordForm';
import { ResetPasswordForm } from './components/auth/ResetPasswordForm';
import { EmailVerification } from './components/auth/EmailVerification';
import { apiService } from './services/apiService';
// Temporarily disabled to fix JS loading issue
// import UpdateNotification from './components/UpdateNotification';
// Force rebuild: 2025-08-05 - Deploy auth endpoint fixes
// Emergency cache refresh: 2025-08-06 - Healthcare data access fix

interface AppProps {}

// Export this for use in other components
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// API service already configured to send cookies via credentials: 'include'

// Protected Route component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useSimpleAuth();
  const location = useLocation();

  // Show loading spinner while authentication is being checked
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log('[ProtectedRoute] User not authenticated, redirecting to login');
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Main dashboard component (previously App content)
const Dashboard: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useSimpleAuth();
  const navigate = useNavigate();
  const { view } = useParams<{ view?: string }>();
  
  // Determine current view from URL
  const currentView = (view === 'trends' || view === 'reports') ? view : 'upload';

  const handleBatchComplete = async (reports: ExtractedReport[]) => {
    if (!user || !isAuthenticated) {
      setError('Not authenticated. Please log in.');
      return;
    }
    
    // Save reports for authenticated user

    try {
      const response = await apiService.saveReportsBatch(
        reports.map(report => ({
          filename: report.filename,
          fileHash: report.fileHash,
          testDate: report.testDate,
          rawText: report.rawText,
          results: report.results.map((r: any) => ({
            testName: r.testName,
            value: r.value,
            units: r.units,
            status: r.status,
            referenceMin: r.referenceMin,
            referenceMax: r.referenceMax,
            confidence: r.confidence,
            originalText: r.originalText || ''
          }))
        }))
      );

      const { status, data, message } = response;
      
      if (status === 'success') {
        // Successfully saved all lab reports
      } else if (status === 'partial_success') {
        let logMsg = `✅ Saved ${data.summary.successfullySaved} of ${data.summary.totalSubmitted} reports.`;
        if (data.summary.duplicates > 0) {
          logMsg += ` ⚠️ ${data.summary.duplicates} duplicate(s) skipped.`;
        }
        if (data.summary.failed > 0) {
          logMsg += ` ❌ ${data.summary.failed} failed.`;
        }
        // Log message: logMsg
        
        if (data.duplicateReports && data.duplicateReports.length > 0) {
          const dupDetails = data.duplicateReports.map((dup: any) => 
            `• ${dup.report.filename}: ${dup.message}`
          ).join('\n');
          // Duplicate reports: dupDetails
        }
      } else if (status === 'all_duplicates') {
        // All reports are duplicates
        
        if (data.duplicateReports && data.duplicateReports.length > 0) {
          const dupList = data.duplicateReports
            .map((dup: any) => dup.report?.filename || 'Unknown file')
            .join(', ');
          // Existing reports: dupList
        }
      } else {
        setError(message || 'Failed to save reports');
      }
      
      if (data.failedReports && data.failedReports.length > 0) {
        // Failed reports in data.failedReports
      }
      
    } catch (err: any) {
      // Error saving reports
      setError(err.response?.data?.message || 'Failed to save reports. Please try again.');
      throw err;
    }
  };

  return (
    <div className="min-h-screen">
      <Header currentView={currentView} onViewChange={(newView) => {
        navigate(`/dashboard${newView !== 'upload' ? `/${newView}` : ''}`);
      }} />
      <div className="container" style={{ paddingTop: '80px' }}>

        {/* Error Display */}
        {error && (
          <div className="warning-card error animate-slide-up max-w-2xl mx-auto mb-6">
            <div className="warning-icon">❌</div>
            <div className="warning-content">
              <p className="warning-message">{error}</p>
            </div>
          </div>
        )}

        {user && (
          <>
            {/* Keep all views mounted but hidden to preserve state */}
            <div style={{ display: currentView === 'upload' ? 'block' : 'none' }}>
              <UploadSession 
                onComplete={handleBatchComplete}
                onCancel={() => {}}
                onNavigateToTrends={() => navigate('/dashboard/trends')}
              />
            </div>
            <div style={{ display: currentView === 'reports' ? 'block' : 'none' }}>
              <ReportsManager 
                onClose={() => navigate('/dashboard')}
              />
            </div>
            <div style={{ display: currentView === 'trends' ? 'block' : 'none' }}>
              <TrendsView 
                onClose={() => navigate('/dashboard')}
              />
            </div>
          </>
        )}
      </div>
      
      {/* Session status indicator for debugging */}
      {process.env.NODE_ENV === 'development' && <SessionStatus />}
      
      {/* Cost tracker for development */}
      {process.env.NODE_ENV === 'development' && <CostTracker />}
    </div>
  );
};

const App: React.FC<AppProps> = () => {

  return (
    <Router>
      {/* Temporarily disabled to fix JS loading issue */}
      {/* <UpdateNotification /> */}
      <Routes>
        {/* Public routes */}
        <Route path="/" element={
          <>
            <Header />
            <LandingPage onGetStarted={() => {}} />
          </>
        } />
        
        {/* Authentication routes */}
        <Route path="/auth/login" element={<LoginForm />} />
        <Route path="/auth/register" element={<RegisterForm />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordForm />} />
        <Route path="/auth/reset-password" element={<ResetPasswordForm />} />
        <Route path="/auth/verify-email" element={<EmailVerification />} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/:view" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        
        {/* Redirect any unknown routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

// Wrap App with providers
const AppWithProviders: React.FC = () => {
  return (
    <SimpleAuthProvider>
      <TrendsDataProvider>
        <App />
      </TrendsDataProvider>
    </SimpleAuthProvider>
  );
};

export default AppWithProviders;